import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { IntegrationKey } from './types';
import { integrationOptions } from './constants';

type ConfirmToggle = {
  type: 'email' | 'app';
  nextValue: boolean;
};

type ConfirmIntegrationToggle = {
  key: IntegrationKey;
  nextValue: boolean;
};

type SettingsOverlaysProps = {
  data: {
    confirmToggle: ConfirmToggle | null;
    confirmIntegrationToggle: ConfirmIntegrationToggle | null;
    isSupportSuccessOpen: boolean;
    snackbarOpen: boolean;
    snackbarMessage: string;
  };
  actions: {
    onCloseConfirmToggle: () => void;
    onConfirmToggle: () => void;
    onCloseConfirmIntegration: () => void;
    onConfirmIntegration: () => void;
    onCloseSupportSuccess: () => void;
    onCloseSnackbar: () => void;
  };
};

const SettingsOverlays = ({ data, actions }: SettingsOverlaysProps) => {
  const {
    confirmToggle,
    confirmIntegrationToggle,
    isSupportSuccessOpen,
    snackbarOpen,
    snackbarMessage,
  } = data;
  const {
    onCloseConfirmToggle,
    onConfirmToggle,
    onCloseConfirmIntegration,
    onConfirmIntegration,
    onCloseSupportSuccess,
    onCloseSnackbar,
  } = actions;
  const integrationLabel = confirmIntegrationToggle
    ? integrationOptions.find((option) => option.key === confirmIntegrationToggle.key)
        ?.label ?? 'this integration'
    : 'this integration';

  return (
    <>
      {confirmToggle ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
          <div className="w-full max-w-[520px] rounded-[18px] bg-white px-6 py-8 text-center shadow-xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-500">
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              {confirmToggle.nextValue
                ? confirmToggle.type === 'email'
                  ? 'Enable Email Notification?'
                  : 'Enable In-App Notification?'
                : confirmToggle.type === 'email'
                  ? 'Disable Email Notification?'
                  : 'Disable In-App Notification?'}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {confirmToggle.nextValue
                ? 'You will start receiving important session updates.'
                : 'You might miss important session updates.'}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                type="button"
                className="min-w-[140px] rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                onClick={onCloseConfirmToggle}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`min-w-[140px] rounded-[12px] px-4 py-2 text-sm font-semibold text-white ${
                  confirmToggle.nextValue ? 'bg-emerald-600' : 'bg-rose-500'
                }`}
                onClick={onConfirmToggle}
              >
                {confirmToggle.nextValue ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmIntegrationToggle ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
          <div className="w-full max-w-[560px] rounded-[18px] bg-white px-6 py-8 text-center shadow-xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-rose-50">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-500">
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              {confirmIntegrationToggle.nextValue
                ? 'Are you sure you want to Connect?'
                : 'Are you sure you want to Disconnect?'}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {confirmIntegrationToggle.nextValue
                ? `You'll be able to sync your calendar with ${integrationLabel}.`
                : `You won't be able to sync your calendar with ${integrationLabel}.`}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                type="button"
                className="min-w-[140px] rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                onClick={onCloseConfirmIntegration}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`min-w-[140px] rounded-[12px] px-4 py-2 text-sm font-semibold text-white ${
                  confirmIntegrationToggle.nextValue
                    ? 'bg-emerald-600'
                    : 'bg-rose-500'
                }`}
                onClick={onConfirmIntegration}
              >
                {confirmIntegrationToggle.nextValue ? 'Connect' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isSupportSuccessOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
          <div className="w-full max-w-[420px] rounded-[18px] bg-white px-6 py-8 text-center shadow-xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
              Query Sent
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Our team will be notified about this issue.
            </p>
            <button
              type="button"
              className="mt-6 w-full rounded-[12px] bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              onClick={onCloseSupportSuccess}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={onCloseSnackbar} severity="error" variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsOverlays;
